import { TABLE_DATA } from '~pages/parents/bulk-upload/constants';
import type { ParentRegistrationColumnMappingTableData } from '~pages/parents/bulk-upload/types';
import type { ParentRegistrationData } from '~pages/parents/bulk-upload/validation';

import type { SourcePlatform } from '~entities/import-job/api/types';

import { resolvePreMappedColumn } from './resolve-pre-mapped-column';

export function getMaxStudentFromHeaders(
  headers: Array<string>,
  familyAliases: Record<string, string> = {},
): number {
  let max = 0;
  const re = /^Student (\d+) /i;

  const aliasBySourceLower = new Map<string, string>();
  for (const [source, target] of Object.entries(familyAliases)) {
    aliasBySourceLower.set(source.trim().toLowerCase(), target);
  }

  for (const h of headers) {
    const trimmed = h.trim();
    const direct = re.exec(trimmed);
    if (direct?.[1]) max = Math.max(max, parseInt(direct[1], 10));

    const aliasTarget = aliasBySourceLower.get(trimmed.toLowerCase());
    const aliasMatch = aliasTarget ? re.exec(aliasTarget) : null;
    if (aliasMatch?.[1]) {
      max = Math.max(max, parseInt(aliasMatch[1], 10));
    }
  }

  return max;
}

export function buildFamilyColumnMappingTable(
  headers: Array<string>,
  csvRow: ParentRegistrationData | undefined,
  familyAliases: Record<string, string>,
  sourcePlatform: SourcePlatform,
): Array<ParentRegistrationColumnMappingTableData> {
  // Upload CSV may use arbitrary headers; always offer at least Student 1.
  const detected = getMaxStudentFromHeaders(headers, familyAliases);
  const maxStudent =
    sourcePlatform === 'csv' ? Math.max(1, detected) : detected;
  return TABLE_DATA.filter((row) => {
    const m = /^Student (\d+) /.exec(row.header);
    if (!m?.[1]) return true;
    return parseInt(m[1], 10) <= maxStudent;
  }).map((row) => {
    // Upload CSV: never auto-select — user maps every column.
    const selectedColumn =
      sourcePlatform === 'csv'
        ? ''
        : resolvePreMappedColumn(
            row.header,
            headers,
            familyAliases,
            sourcePlatform,
          );
    return {
      ...row,
      selectedColumn,
      columnValue:
        selectedColumn && csvRow
          ? String(csvRow[selectedColumn as keyof ParentRegistrationData] ?? '')
          : '',
    };
  });
}
